import { NextPage } from 'next'
import { SSRConfig, appWithTranslation } from 'next-i18next/pages'
import nextI18NextConfig from '../../next-i18next.config'
import NextApp from 'next/app'
import type { AppContext, AppInitialProps, AppProps } from 'next/app'
import { Router } from 'next/router'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'
import '../../styles/nprogress.css'
import { ReactElement, ReactNode, useEffect } from 'react'
import { avertaFont, FontVariableEnum, interFont } from 'shared/constants/fonts'
import DashboardLayout from 'shared/layout/components/dashboard-layout'
import { RootProviders } from 'shared/providers/root-providers'
import '../../styles/globals.css'
import {
  DeviceTypeEnum,
  MetaDataFromServerInterface,
} from 'shared/hooks/use-metadata-from-server'
import { getServerDeviceType } from 'shared/utils/device/get-server-device-type'
// import { VersionMonitor } from 'shared/components/version-monitor'

export type NextPageWithLayout<P = {}, R = P> = NextPage<PageProps> & {
  getLayout: (page: ReactElement<any>, browserTabTitle: PageProps['browserTabTitle']) => ReactNode
  getSecondaryLayout: (page: ReactElement<P>) => ReactElement<R>
}

export interface PageProps {
  browserTabTitle: string
}

type AppPropsWithLayout = AppProps<PageProps> &
  MetaDataFromServerInterface & {
    pageProps: SSRConfig
  } & {
    Component: NextPageWithLayout
  }

const getDefaultPageLayout = (
  page: ReactElement<any>,
  browserTabTitle: PageProps['browserTabTitle'],
) => <DashboardLayout browserTabTitle={browserTabTitle}>{page}</DashboardLayout>

function App({ Component, pageProps, hostOnServer, serverDeviceType }: AppPropsWithLayout) {
  useEffect(() => {
    const handleRouteStart = () => NProgress.start()
    const handleRouteDone = () => NProgress.done()

    Router.events.on('routeChangeStart', handleRouteStart)
    Router.events.on('routeChangeComplete', handleRouteDone)
    Router.events.on('routeChangeError', handleRouteDone)

    return () => {
      Router.events.off('routeChangeStart', handleRouteStart)
      Router.events.off('routeChangeComplete', handleRouteDone)
      Router.events.off('routeChangeError', handleRouteDone)
    }
  }, [])

  const getLayout = Component.getLayout ?? getDefaultPageLayout
  const getSecondaryLayout = Component.getSecondaryLayout ?? (page => page)

  return (
    <>
      <style jsx global>{`
        :root {
          ${FontVariableEnum.Inter}: ${interFont.style.fontFamily};
          ${FontVariableEnum.Averta}: ${avertaFont.style.fontFamily};
        }
      `}</style>
      {/*<Script*/}
      {/*  strategy="beforeInteractive"*/}
      {/*  crossOrigin="anonymous"*/}
      {/*  src="https://unpkg.com/date-time-format-timezone@latest/build/browserified/date-time-format-timezone-complete-min.js"*/}
      {/*/>*/}
      <RootProviders hostOnServer={hostOnServer} serverDeviceType={serverDeviceType}>
        {/*<VersionMonitor />*/}
        {getLayout(getSecondaryLayout(<Component {...pageProps} />), pageProps.browserTabTitle)}
      </RootProviders>
    </>
  )
}

App.getInitialProps = async (
  context: AppContext,
): Promise<MetaDataFromServerInterface & AppInitialProps> => {
  const ctx = await NextApp.getInitialProps(context)
  const { req, res } = context.ctx

  const hostOnServer = req?.headers?.host || null
  const serverDeviceType = req
    ? getServerDeviceType(req.headers['user-agent'])
    : DeviceTypeEnum.Unknown

  if (req && res && req.url) {
    if (res.statusCode === 404) {
      const accept = req.headers['accept'] || ''
      const isHtmlRequest = accept.includes('text/html')

      // Only redirect actual page requests, not Next.js data fetching
      if (isHtmlRequest) {
        context.ctx.res?.writeHead(302, {
          Location: '/dashboard/not-found',
        })
        context.ctx.res?.end()
      }
    }
  }

  return { ...ctx, hostOnServer, serverDeviceType }
}

export default appWithTranslation(App, nextI18NextConfig)
